import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronRight, Scale, Search, Compass, ClipboardCheck, Map, FolderKanban, Sparkles, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/agreements/library", label: "Library", icon: Search },
  { to: "/agreements/finder", label: "Find a document", icon: Compass },
  { to: "/agreements/plan", label: "My plan", icon: FolderKanban },
  { to: "/agreements/checklist", label: "Checklist", icon: ClipboardCheck },
  { to: "/agreements/roadmap", label: "Roadmap", icon: Map },
  { to: "/agreements/templates", label: "Templates", icon: FileText },
];

/**
 * AgreementsShell — sub-nav + hero frame for every CPG Agreements page.
 *
 * Styled to match the Welcome page: a dark hero card with a 4-color gradient
 * top stripe, a pink small-caps eyebrow, and a rose→amber→violet→sky underline
 * that ties every heading in the module together. Sub-nav sits inside a soft
 * card so the module reads as a coherent surface, not a bare page.
 */
export default function AgreementsShell({
  crumbs = [],
  title,
  subtitle,
  eyebrow,
  actions,
  children,
  wide = false,
  hero = true,
}) {
  const eyebrowText = (eyebrow || (crumbs.length ? crumbs[crumbs.length - 1]?.label : "CPG Agreements") || "CPG Agreements").toString();

  return (
    <div className="w-full overflow-x-clip">
      {/* ── Sub-nav card (welcome-page-style soft surface) ──────────── */}
      <div className="mb-6 rounded-2xl border bg-gradient-to-r from-rose-50 via-white to-indigo-50 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-900 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 sm:px-4">
          <Link
            to="/agreements"
            className="inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold text-neutral-900 hover:bg-white/70 hover:shadow-sm dark:text-neutral-100 dark:hover:bg-white/10"
            data-testid="agreements-home-link"
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 via-fuchsia-500 to-indigo-500 text-white">
              <Scale className="h-3.5 w-3.5" />
            </span>
            CPG Agreements
          </Link>
          <span className="hidden h-4 w-px bg-neutral-300/70 sm:block" />
          <nav className="flex items-center gap-1 overflow-x-auto" aria-label="CPG Agreements">
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-neutral-600 hover:bg-white/70 hover:text-neutral-900 hover:shadow-sm transition-colors dark:text-neutral-300 dark:hover:bg-white/10 dark:hover:text-white",
                    // Active pill uses the module's rose→fuchsia→indigo gradient
                    // (same as the "CPG Agreements" home badge) so the selected
                    // menu stays clearly visible against the shell's rose-tinted
                    // gradient background — plain `bg-white` blended in and read
                    // as unselected against that light backdrop.
                    isActive && "bg-gradient-to-br from-rose-500 via-fuchsia-500 to-indigo-500 text-white shadow-md hover:bg-white/0 hover:text-white dark:text-white dark:hover:bg-white/0",
                  )
                }
              >
                <Icon className="h-4 w-4" /> <span className="hidden sm:inline">{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      <div className="w-full">
        {crumbs.length > 0 && (
          <nav className="mb-3 flex flex-wrap items-center gap-1 text-xs text-neutral-500" aria-label="Breadcrumb">
            <Link to="/agreements" className="hover:text-neutral-900 dark:hover:text-neutral-100">
              CPG Agreements
            </Link>
            {crumbs.map((c, i) => (
              <React.Fragment key={i}>
                <ChevronRight className="h-3 w-3" />
                {c.to ? (
                  <Link to={c.to} className="hover:text-neutral-900 dark:hover:text-neutral-100">
                    {c.label}
                  </Link>
                ) : (
                  <span className="text-neutral-900 dark:text-neutral-100">{c.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}

        {/* ── Hero card — Welcome-page style ─────────────────────────── */}
        {(title || actions) && hero && (
          <section
            className="mb-8 overflow-hidden rounded-2xl border border-neutral-800/40 bg-neutral-900 text-white shadow-lg"
            data-testid="agreements-hero"
          >
            {/* 4-color gradient stripe on top (rose → amber → violet → sky) */}
            <div className="h-1 w-full bg-gradient-to-r from-rose-500 via-pink-400 via-fuchsia-500 to-sky-500" />
            <div className="flex flex-wrap items-start justify-between gap-4 p-5 sm:p-8">
              <div className="min-w-0 flex-1">
                <p className="mb-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-400">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>{eyebrowText}</span>
                </p>
                {title && (
                  <h1 className="text-2xl font-bold tracking-tight text-white sm:text-4xl">
                    {title}
                  </h1>
                )}
                {/* rose→amber→sky underline echoing the module palette */}
                <span className="mt-3 block h-0.5 w-16 rounded-full bg-gradient-to-r from-rose-500 via-fuchsia-500 to-sky-500" aria-hidden="true" />
                {subtitle && (
                  <p className="mt-4 max-w-2xl text-sm text-neutral-300 sm:text-base">{subtitle}</p>
                )}
              </div>
              {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
            </div>
          </section>
        )}

        {/* ── Plain header (opt-out via hero=false), preserved for edge cases ─ */}
        {(title || actions) && !hero && (
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              {title && <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>}
              {subtitle && <p className="mt-1.5 max-w-2xl text-sm text-neutral-500">{subtitle}</p>}
            </div>
            {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
          </div>
        )}

        {children}
      </div>
    </div>
  );
}
